import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie } from 'lucide-react';

export default function CookiesPolicy() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-surface-2">
      {/* Header */}
      <div className="bg-surface shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-accent hover:underline mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back', 'Volver')}
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
              <Cookie className="w-6 h-6 text-accent" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-ink">
              {t('legal.cookiesPolicy.title', 'Política de Cookies')}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-surface rounded-xl shadow-sm p-6 sm:p-8 space-y-6">
          <p className="text-sm text-ink-muted">
            {t('legal.lastUpdated', 'Última actualización')}: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.cookiesPolicy.whatAreCookies.title', '¿Qué son las cookies?')}
            </h2>
            <p className="text-ink-muted">
              {t('legal.cookiesPolicy.whatAreCookies.content', 'Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando visitas un sitio web. Estas nos permiten recordar tus preferencias y mejorar tu experiencia de navegación.')}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.cookiesPolicy.cookiesWeUse.title', 'Cookies que utilizamos')}
            </h2>
            <p className="text-ink-muted mb-4">
              {t('legal.cookiesPolicy.cookiesWeUse.intro', 'En Monkey Barber utilizamos únicamente cookies estrictamente necesarias para el funcionamiento de la aplicación:')}
            </p>
            <div className="bg-surface-2 rounded-lg p-4 space-y-4">
              <div>
                <h3 className="font-medium text-ink">
                  {t('legal.cookiesPolicy.cookiesWeUse.essential.title', 'Cookies esenciales')}
                </h3>
                <p className="text-sm text-ink-muted mt-1">
                  {t('legal.cookiesPolicy.cookiesWeUse.essential.content', 'Necesarias para el funcionamiento básico del sitio. Incluyen cookies de autenticación y sesión que permiten mantener tu sesión iniciada de forma segura.')}
                </p>
              </div>
              <div>
                <h3 className="font-medium text-ink">
                  {t('legal.cookiesPolicy.cookiesWeUse.preferences.title', 'Cookies de preferencias')}
                </h3>
                <p className="text-sm text-ink-muted mt-1">
                  {t('legal.cookiesPolicy.cookiesWeUse.preferences.content', 'Almacenan tus preferencias como el idioma seleccionado y el tema (claro/oscuro) para personalizar tu experiencia.')}
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.cookiesPolicy.noCookies.title', 'Cookies que NO utilizamos')}
            </h2>
            <ul className="list-disc list-inside text-ink-muted space-y-2">
              <li>{t('legal.cookiesPolicy.noCookies.analytics', 'No utilizamos cookies de análisis o estadísticas de terceros')}</li>
              <li>{t('legal.cookiesPolicy.noCookies.advertising', 'No utilizamos cookies publicitarias ni de seguimiento')}</li>
              <li>{t('legal.cookiesPolicy.noCookies.thirdParty', 'No compartimos datos con redes publicitarias')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.cookiesPolicy.manageCookies.title', 'Gestión de cookies')}
            </h2>
            <p className="text-ink-muted">
              {t('legal.cookiesPolicy.manageCookies.content', 'Puedes configurar tu navegador para bloquear o eliminar cookies. Sin embargo, ten en cuenta que algunas funciones del sitio podrían no funcionar correctamente sin las cookies esenciales.')}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.cookiesPolicy.contact.title', 'Contacto')}
            </h2>
            <p className="text-ink-muted">
              {t('legal.cookiesPolicy.contact.content', 'Si tienes preguntas sobre nuestra política de cookies, puedes contactarnos a través de nuestra página de contacto.')}
            </p>
          </section>

          {/* Links to other policies */}
          <div className="border-t border-hairline pt-6 mt-8">
            <h3 className="text-sm font-medium text-ink mb-3">
              {t('legal.relatedPolicies', 'Políticas relacionadas')}
            </h3>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/privacy-policy"
                className="text-accent hover:underline text-sm"
              >
                {t('cookies.privacyPolicy', 'Política de Privacidad')}
              </Link>
              <Link
                to="/terms-of-service"
                className="text-accent hover:underline text-sm"
              >
                {t('cookies.termsOfService', 'Términos de Servicio')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
